/**
 * DialogueManager.js
 * Manages the "Conversation" between orchestral sections.
 * Prevents "Wall of Sound" by ensuring Call & Response logic across roles.
 */

export class DialogueManager {
    constructor() {
        // Timeline of activity: [{ role, start, end, intensity }]
        this.activityLog = [];
    }

    /**
     * Registers a period of activity for a specific role.
     * @param {string} role - e.g., 'strings', 'winds', 'brass'
     * @param {number} start - Start beat
     * @param {number} duration - Duration in beats
     * @param {number} intensity - 0.0 to 1.0
     */
    registerActivity(role, start, duration, intensity = 0.5) {
        this.activityLog.push({
            role,
            start,
            end: start + duration,
            intensity
        });
    }

    /**
     * Analyzes the timeline to determine what the current role should do.
     * @param {string} myRole - The role querying for instructions.
     * @param {number} time - The current time (beat).
     * @returns {string} Suggestion: 'call', 'response', 'support', 'tacit'
     */
    getSuggestion(myRole, time) {
        // Look at recent activity (last 4 beats)
        const windowStart = Math.max(0, time - 4.0);
        const recent = this.activityLog.filter(a => a.end > windowStart && a.start < time);

        // Filter out self
        const others = recent.filter(a => a.role !== myRole);

        if (others.length === 0) {
            // Silence? Take the lead.
            return 'call';
        }

        // Who is active?
        // Sort by end time descending (most recent finisher)
        others.sort((a,b) => b.end - a.end);
        const lastEvent = others[0];

        // If someone is currently playing (overlapping)
        if (lastEvent.end > time) {
            // Overlap logic
            if (myRole === 'brass' && (lastEvent.role === 'strings' || lastEvent.role === 'winds')) {
                // Brass shouldn't step on others unless Climax
                return 'support'; // Low volume / pads
            }
            if (myRole === 'winds' && lastEvent.role === 'strings') {
                return 'support'; // Counter-melody or color
            }
            // If heavy overlap, maybe Tacit to clear mud
            if (lastEvent.intensity > 0.8) return 'tacit';

            return 'support';
        }

        // If someone JUST finished (within 1 beat)
        if (time - lastEvent.end < 1.0) {
            // Perfect time for Response
            return 'response';
        }

        return 'call';
    }

    /**
     * Pre-analyzes abstract tracks to populate the log.
     * @param {Object} abstractTracks - Map of role -> event array
     * @param {Object} allocationMap - Map of abstract role -> assigned instruments
     */
    analyzeTracks(abstractTracks, allocationMap) {
        this.activityLog = [];

        Object.entries(abstractTracks).forEach(([absRole, events]) => {
            if (!events || events.length === 0) return;

            // Map abstract role to Section Family (Strings, Winds, Brass)
            // This requires looking at the allocation.
            // Simplified: Use abstract role names if they are descriptive, or guess.
            // Abstract roles are 'melody', 'harmony', 'bass'.
            // We need to know WHO is playing 'melody'.

            // This method might be called AFTER allocation, so we can cross ref.

            const assignments = allocationMap[absRole];
            if (!assignments || assignments.length === 0) return;

            // Take the first instrument's family
            const inst = assignments[0].instrument;
            let family = 'other';
            if (inst.id.includes('violin') || inst.id.includes('cello') || inst.id.includes('viola') || inst.id.includes('bass')) family = 'strings';
            else if (inst.id.includes('trumpet') || inst.id.includes('horn') || inst.id.includes('tuba') || inst.id.includes('trombone')) family = 'brass';
            else if (inst.id.includes('flute') || inst.id.includes('oboe') || inst.id.includes('clarinet') || inst.id.includes('bassoon')) family = 'winds';

            // Cluster events into phrases
            // If gaps < 1.0, treat as one phrase
            let currentPhrase = null;

            events.sort((a,b) => a.s - b.s).forEach(ev => {
                if (!currentPhrase) {
                    currentPhrase = { start: ev.s, end: ev.s + ev.len, intensity: ev.vel || 0.5 };
                } else {
                    if (ev.s - currentPhrase.end < 1.5) {
                        // Extend phrase
                        currentPhrase.end = Math.max(currentPhrase.end, ev.s + ev.len);
                        currentPhrase.intensity = Math.max(currentPhrase.intensity, ev.vel || 0.5);
                    } else {
                        // Commit previous
                        this.registerActivity(family, currentPhrase.start, currentPhrase.end - currentPhrase.start, currentPhrase.intensity);
                        currentPhrase = { start: ev.s, end: ev.s + ev.len, intensity: ev.vel || 0.5 };
                    }
                }
            });
            if (currentPhrase) {
                this.registerActivity(family, currentPhrase.start, currentPhrase.end - currentPhrase.start, currentPhrase.intensity);
            }
        });
    }
}
